/**
 * Novera Pro — the ONE status registry (ux/01 §2.3, §3.10; D-23).
 *
 * Six tones, never a seventh. A status keeps the same word, tone and icon on
 * every screen: a screen renders `<Badge status="facture_achat.VALIDE" />` (or
 * `resolveStatus(domain, value)`) and never picks a colour itself. Module work
 * packages ADD their statuses here, in the same change, rather than defining a
 * local map (ux/01 §2.3 last paragraph).
 *
 * Enum values are the Prisma enum members (apps/server/prisma/schema.prisma)
 * where the status is stored; derived states (lot « Épuisé », the écart chips
 * of D-09, the CHIFA bridge state) use an upper-snake key of their own. The
 * domain key names the document. The D-17 bon de commande lifecycle landed
 * with its screens (WP-33); the D-20 réclamation one with its pharmacy screens (WP-35).
 */

/** The six tones and their default lucide icon. Théorique's hourglass is reserved. */
export const STATUS_TONES = Object.freeze({
  neutral: Object.freeze({ icon: "circle-dashed", label: "Neutre" }),
  info: Object.freeze({ icon: "clock", label: "Information" }),
  warning: Object.freeze({ icon: "alert-triangle", label: "Attention" }),
  success: Object.freeze({ icon: "check-circle-2", label: "Terminé" }),
  danger: Object.freeze({ icon: "x-circle", label: "Bloquant" }),
  theorique: Object.freeze({ icon: "hourglass", label: "Théorique" }),
});

export const STATUS_TONE_NAMES = Object.freeze(Object.keys(STATUS_TONES));

/**
 * Tones that carry a leading icon even when the status has none of its own
 * (shape redundancy, P-05). Neutral and info read by their word alone.
 */
export const TONES_WITH_ICON = Object.freeze([
  "success",
  "warning",
  "danger",
  "theorique",
]);

/**
 * Legacy tone names still passed by existing call sites (`Badge tone="accent"`,
 * `WORKFLOW_STATES` entries) → the six tones.
 */
export const LEGACY_TONE_MAP = Object.freeze({
  accent: "success",
  default: "neutral",
  gray: "neutral",
  green: "success",
  amber: "warning",
  red: "danger",
  blue: "info",
  purple: "theorique",
  theoretical: "theorique",
});

const s = (label, tone, icon, extra = {}) =>
  Object.freeze({ label, tone, ...(icon ? { icon } : {}), ...extra });

/**
 * domain → enum value → { label, tone, icon?, strike? }.
 * `strike` draws the document number struck through (Annulé, ux/01 §2.3).
 */
export const STATUS_REGISTRY = Object.freeze({
  // StatutAchat — facture d'achat
  facture_achat: Object.freeze({
    BROUILLON: s("Brouillon", "neutral"),
    VALIDE: s("Validée", "success"),
    AVOIR: s("Avoir", "info"),
    ANNULE: s("Annulée", "neutral", "ban", { strike: true }),
    RECTIFIE: s("Rectifiée", "info"),
  }),
  // StatutReglementAchat — mirrors STATUT_REGLEMENT_TONS (reglement-achat.config.js)
  reglement_facture: Object.freeze({
    NON_REGLEE: s("Non réglée", "warning"),
    PARTIELLEMENT_REGLEE: s("Réglée partiellement", "info"),
    REGLEE: s("Réglée", "success"),
  }),
  // StatutPaiementAvoir — the avoir triad (03 §2.4, D-12)
  avoir_paiement: Object.freeze({
    EN_ATTENTE: s("En attente", "warning", "clock"),
    PAYE: s("Réglé", "success"),
    RECU: s("Réglé", "success"),
  }),
  // Refonte WP-30 — the derived état of an avoir (ux/03 §2.4, OP-03-08): journal chips, column,
  // drawer. Réglé partiellement = the half-filled circle (01 §2.3 `contrast`), Annulé struck.
  // Refonte WP-83 (D-32, CP §3.2): before anything is received, the état says the credit's lifecycle —
  // « En instance » (outside the solde, awaiting the fournisseur's confirmation) or « Confirmé » (in the
  // solde, the fournisseur's document still awaited; a ristourne not yet encaissée). EN_ATTENTE stays
  // the server's filter value (both of them) until the journal reader serves the two (EXE-106).
  avoir_etat: Object.freeze({
    INSTANCE: s("En instance", "warning", "clock"),
    CONFIRME: s("Confirmé", "info", "file-check-2"),
    EN_ATTENTE: s("En attente", "warning", "clock"),
    REGLE_PARTIELLEMENT: s("Réglé partiellement", "info", "contrast", { short: "Réglé part." }),
    REGLE: s("Réglé", "success"),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // Cross-module X §6 · XWP-20 (XD-163) — the derived état of a client crédit (journal, drawer).
  credit_etat: Object.freeze({
    OUVERT: s("Ouvert", "warning", "clock"),
    PARTIELLEMENT_REGLE: s("Partiellement réglé", "info", "contrast", { short: "Part. réglé" }),
    REGLE: s("Réglé", "success"),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // Cross-module X §7 · XWP-21 — the derived état of an Instance à terme CHIFA (journal chips, column,
  // drawer): the lifecycle status, or Supprimée when the instance was deleted (number kept, struck).
  instance_etat: Object.freeze({
    PENDING: s("En attente", "warning", "clock"),
    SENT_CHIFA: s("Envoyée à CHIFA", "info", "send", { short: "Envoyée" }),
    SETTLED: s("Réglée", "success"),
    // X §8.1 · XWP-22 — paid as a normal Vente libre (« Envoyer vente libre »)
    REGLEE_VENTE_LIBRE: s("Réglée en vente libre", "success", undefined, { short: "Vente libre" }),
    ANNULEE: s("Supprimée", "neutral", "ban", { strike: true }),
  }),
  // X §4.4 · XWP-23 — the client motif (Prisma MotifClient; a NULL motif reads NORMAL)
  client_motif: Object.freeze({
    MC: s("Maladie chronique", "info", "heart-pulse", { short: "MC" }),
    NORMAL: s("Normal", "neutral"),
  }),
  // ux/03 §2.7 (A-02) — matching of a connected fournisseur's credit note
  note_rapprochement: Object.freeze({
    A_RAPPROCHER: s("À rapprocher", "warning"),
    RAPPROCHE: s("Rapproché", "success"),
  }),
  // Refonte WP-31 (ux/03 §4.2, OP-03-22) — the état of a bon de retour FOURNISSEUR (derived from
  // StatutBonRetour + the live avoir credits). Distinct from `bon_retour` below (customer returns).
  bon_retour_fournisseur: Object.freeze({
    BROUILLON: s("Brouillon", "neutral", "pencil"),
    AVOIR_EN_ATTENTE: s("Avoir en attente", "warning", "clock"),
    AVOIR_CREE: s("Avoir créé", "success"),
    // refonte WP-95 (A-122 (4)) — validated « Sans impact sur le solde »: no avoir awaited
    SANS_IMPACT: s("Sans impact sur le solde", "neutral", "circle-slash", { short: "Sans impact" }),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // Refonte WP-33 (ux/03 §3.1, D-17) — StatutBonCommandePharmacie, the pharmacy lifecycle of a bon de
  // commande (journal chips, column, document title band, S1 picker). The supplier acknowledgement is
  // the separate « Accusé fournisseur » mark (words next to the badge), never a second badge colour.
  bon_commande: Object.freeze({
    BROUILLON: s("Brouillon", "neutral", "pencil"),
    ENVOYE: s("Envoyé", "info", "send"),
    PARTIELLEMENT_RECU: s("Partiellement reçu", "warning", "contrast", { short: "Part. reçu" }),
    RECU: s("Reçu", "success"),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // Refonte WP-35 (ux/03 §5.1, D-20) — StatutReclamation on both planes (journal, document, B2 drawer).
  // En cours carries the clock (the hourglass is Théorique's, ux/01 §2.3); Clôturée reads muted.
  reclamation: Object.freeze({
    BROUILLON: s("Brouillon", "neutral", "pencil"),
    ENVOYEE: s("Envoyée", "info", "send"),
    EN_COURS: s("En cours", "info", "clock"),
    RESOLUE: s("Résolue", "success"),
    REJETEE: s("Rejetée", "danger"),
    CLOTUREE: s("Clôturée", "neutral", "archive"),
  }),
  // Cross-module X XWP-16 — StatutTransfertStock, an intra-pharmacy transfer between magasins
  transfert_stock: Object.freeze({
    BROUILLON: s("Brouillon", "neutral", "circle-dashed"),
    VALIDE: s("Validé", "success"),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // Cross-module X XWP-16 — StatutMagasin (a closed magasin keeps its units, receives none)
  magasin: Object.freeze({
    ACTIF: s("Actif", "success"),
    FERME: s("Fermé", "neutral", "lock"),
  }),
  // StatutRetour — bon de retour (customer returns)
  bon_retour: Object.freeze({
    VALIDE: s("Validé", "success"),
    ANNULE: s("Annulé", "neutral", "ban", { strike: true }),
  }),
  // StatutStockLot
  lot: Object.freeze({
    DISPONIBLE: s("Vendable", "success"),
    RESERVE: s("Réservé", "info"),
    PERIME: s("Périmé", "danger"),
    BLOQUE: s("Bloqué", "neutral", "lock"),
    QUARANTAINE: s("Quarantaine", "danger"),
    EPUISE: s("Épuisé", "neutral"),
  }),
  // D-06 — revenue and margin
  revenu: Object.freeze({
    REEL: s("Réel", "success"),
    THEORIQUE: s("Théorique", "theorique"),
  }),
  // D-06 — the CNAS share of a vente CHIFA (refonte WP-43, ux/05 §4.7; server STATUT_CNAS,
  // utils/pricing/constantes.js): théorique until the bordereau is paid — every value but
  // EN_ATTENTE arrives with the bordereau lifecycle (M-15).
  statut_cnas: Object.freeze({
    EN_ATTENTE: s("CNAS en attente", "theorique", "hourglass"),
    DEPOSEE: s("CNAS déposée", "theorique", "hourglass"),
    PAYEE: s("CNAS payée", "success"),
    PAYEE_AJUSTEE: s("CNAS payée (ajustée)", "success"),
    PAYEE_GLOBAL: s("CNAS payée (globale)", "success"),
    REJETEE: s("CNAS rejetée", "danger", "x-circle"),
  }),
  // Rapprochement écarts (D-09)
  ecart: Object.freeze({
    CONFORME: s("Conforme", "success"),
    AVOIR_A_CREER: s("Avoir financier à créer", "warning"),
    TOTAL_INFERIEUR: s("Total fournisseur inférieur", "warning"),
  }),
  // StatutOperationTresorerie — opération de trésorerie (Trésorerie t01, TD-06 (b);
  // 03_UX_DESIGN X-06: Annulée struck and labelled, Brouillon muted « prévu »)
  operation_tresorerie: Object.freeze({
    BROUILLON: s("Brouillon", "neutral", "circle-dashed"),
    CONFIRME: s("Confirmée", "success"),
    ANNULE: s("Annulée", "neutral", "ban", { strike: true }),
  }),
  // Trésorerie TWP-20 (UX 02 §1.4) — the secondary chips of an operation, next to its status badge:
  // edited after creation (opens « Historique »), a caissier draft P / M must confirm (TD-19), a
  // récurrent occurrence generated late (TD-88), both sides officine accounts (TD-124).
  operation_tresorerie_marque: Object.freeze({
    MODIFIEE: s("Modifiée", "info", "history"),
    A_VALIDER: s("À valider par le propriétaire", "warning", "alert-triangle"),
    RATTRAPAGE: s("Rattrapage", "info", "rotate-ccw"),
    INTERNE: s("Mouvement interne", "info", "arrow-left-right"),
    // A header rebuilt from the legacy journal (TWP-13, 02A §13.3) — « Opération reconstituée ».
    REPRISE: s("Reprise", "info", "archive"),
  }),
  // E-6 DueChip (UX 02 §14.2) — a Brouillon's announced date against the officine's today: late is a
  // warning (never auto-confirmed, TD-89), today information, later neutral; the word carries the days.
  echeance_operation: Object.freeze({
    EN_RETARD: s("En retard", "warning", "alert-triangle"),
    AUJOURDHUI: s("Aujourd'hui", "info", "clock"),
    A_VENIR: s("À venir", "neutral", "calendar-clock"),
  }),
  // StatutChargePharmacie — Trésorerie TWP-27 (UX 03 §13.2): what is still owed reads as a warning, a
  // partial payment as progress, paid as done, cancelled struck (the charge stays visible, never deleted).
  charge_pharmacie: Object.freeze({
    A_PAYER: s("À payer", "warning", "circle"),
    PARTIELLEMENT_PAYEE: s("Partiellement payée", "warning", "circle-dashed"),
    PAYEE: s("Payée", "success"),
    ANNULEE: s("Annulée", "neutral", "ban", { strike: true }),
  }),
  // Refonte WP-80 (D-37, A-106 (4)) — the état of a motif of the two supplier-document catalogues,
  // derived from `actif` + `archive_at` (shared `etatMotif`; words = LIBELLES_ETAT_MOTIF of the shared
  // twin, pinned by the section's test): « Désactivé » leaves the pickers and may come back,
  // « Archivé » is terminal and still read on its documents.
  motif_fournisseur: Object.freeze({
    ACTIF: s("Actif", "success"),
    INACTIF: s("Désactivé", "neutral", "eye-off"),
    ARCHIVE: s("Archivé", "neutral", "archive"),
  }),
  // Trésorerie TWP-18 — the état of a money location in « Comptes & caisses » (UX 02 §1.4): one
  // registry, no seventh tone; « Sous le seuil » / « Solde négatif » are derived from the server flags.
  compte_tresorerie: Object.freeze({
    ACTIF: s("Actif", "success"),
    INACTIF: s("Inactif", "neutral", "eye-off"),
    SOUS_LE_SEUIL: s("Sous le seuil", "warning"),
    SOLDE_NEGATIF: s("Solde négatif", "danger"),
  }),
  // StatutCaisse — a comptoir session (UX 02 §1.4: « Session ouverte » info, « Fermée » neutral)
  session_caisse: Object.freeze({
    OUVERTE: s("Session ouverte", "info", "clock"),
    CLOTUREE: s("Fermée", "neutral"),
  }),
  // Trésorerie TWP-18 (E-5 EcartText, UX 02 §9.5, RC-32) — words first: a shortage or an excess is a
  // warning with its word, never an inverted colour; « Conforme » within the officine's tolerance.
  ecart_caisse: Object.freeze({
    CONFORME: s("Conforme", "success"),
    MANQUE: s("Manque", "warning"),
    EXCEDENT: s("Excédent", "warning"),
  }),
  // Trésorerie TWP-19 (UX 02 §1.4, TA-71 (5)) — the server's classement of a close écart against the
  // officine's tolerance (TD-23 / TDB-01): within it is « conforme » (success, the exact 0 and the
  // minor écart alike), beyond it « Écart à vérifier » — a warning, never danger: an anomaly to
  // verify, not an accusation (03_UX §5.6, brief §34).
  classement_ecart_caisse: Object.freeze({
    CONFORME: s("Conforme", "success", "check-circle-2"),
    MINEUR: s("Écart mineur", "success", "check-circle-2"),
    A_VERIFIER: s("Écart à vérifier", "warning", "alert-triangle"),
  }),
  // Refonte WP-27 (A-19) — delivery record of a document e-mailed to a fournisseur (API words of
  // packages/shared/constants/documents-sortants.js STATUT_ENVOI, the server's twin)
  envoi_document: Object.freeze({
    EN_COURS: s("Envoi en cours", "info", "clock"),
    ENVOYE: s("Envoyé", "success"),
    BAC_A_SABLE: s("Test, non remis", "warning", "flask-conical"),
    ECHEC: s("Échec d'envoi", "danger"),
    NON_ENVOYE: s("Non envoyé", "danger"),
    INTERROMPU: s("Interrompu", "warning"),
  }),
  // Pont CHIFA (05)
  pont_chifa: Object.freeze({
    ACTIF: s("Pont CHIFA actif", "success"),
    HORS_LIGNE: s("Pont CHIFA hors ligne", "warning"),
    NON_INSTALLE: s("Pont CHIFA non installé", "neutral"),
  }),
});

/** Map any tone name (six tones or a legacy alias) to one of the six tones. */
export function normalizeTone(tone) {
  if (tone && STATUS_TONES[tone]) return tone;
  return LEGACY_TONE_MAP[tone] ?? "neutral";
}

/**
 * Resolve a registered status. Accepts `("facture_achat", "VALIDE")` or the
 * dotted key `"facture_achat.VALIDE"`.
 * @returns {{ label: string, tone: string, icon: string|null, strike: boolean } | null}
 *   null when the status is not registered (the caller shows the raw value in
 *   a neutral badge — a missing entry is a registry bug to fix, not a colour
 *   to invent at the call site).
 */
export function resolveStatus(domain, value) {
  let d = domain;
  let v = value;
  if (v === undefined && typeof d === "string" && d.includes(".")) {
    [d, v] = d.split(".");
  }
  const def = STATUS_REGISTRY[d]?.[v];
  if (!def) return null;
  return {
    label: def.label,
    tone: def.tone,
    icon: def.icon ?? statusIconFor(def.tone),
    strike: Boolean(def.strike),
    // the compact label of a narrow column / chip (« Réglé part. », ux/03 §2.2), when the row has one
    ...(def.short ? { short: def.short } : {}),
  };
}

/** The leading icon a tone draws by default (null for neutral and info). */
export function statusIconFor(tone) {
  const t = normalizeTone(tone);
  return TONES_WITH_ICON.includes(t) ? STATUS_TONES[t].icon : null;
}
